import React from 'react';
import { styled } from 'linaria/react';
import { theme } from '../../../Theme';
import SectionWrapper from '../../../Layout/SectionWrapper';
import Image from '../components/Image';

const TextAndImageWrapper = styled(SectionWrapper)`
  width: 100%;
  max-width: 1440px;
  display: flex;
  margin: 0 auto;
  padding: clamp(40px, 4vw, 80px) 32px;

  ${theme.above.md} {
    flex-direction: row;
  }

  ${theme.below.md} {
    flex-direction: column;
  }
`;

const ContentWrapper = styled('div')`
  display: flex;
  gap: clamp(50px, 2vw, 100px);
  width: 100%;
  flex-direction: ${({ position }) =>
    position === 'right' ? 'row-reverse' : 'row'};

  ${theme.below.md} {
    flex-direction: column;
  }
`;

const TextWrapper = styled('div')`
  width: 50%;
  display: flex;
  flex-direction: column;
  flex-shrink: 4;

  ${theme.below.md} {
    width: 100%;
  }
`;

const ImageWrapper = styled('div')`
  width: 50%;
  aspect-ratio: ${({ ratio }) => (ratio === '4:5' ? '4 / 5' : '1 / 1')};

  ${theme.above.md} {
    flex-shrink: 1;
  }

  ${theme.below.md} {
    width: 100%;
  }

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
`;

const TextBlockItem = styled('p')`
  font-family: ${theme.fonts.primary};
  font-size: clamp(10px, 1.5vw, 16px);
  font-weight: 400;
  letter-spacing: -2;
  margin-bottom: clamp(12px, 2vw, 32px);
`;

const Heading = styled('h2')`
  font-family: ${theme.fonts.primary};
  font-size: clamp(12px, 2vw, 18px);
  font-weight: 600;
  margin-top: clamp(8px, 1.5vw, 16px);
`;

const QuoteText = styled('h1')`
  font-family: ${theme.fonts.secondary};
  font-size: clamp(18px, 3vw, 48px);
  font-weight: 325;
  line-height: clamp(1.2, 3vw, 1.6);
`;

const LargeText = styled('h1')`
  font-family: ${theme.fonts.secondary};
  font-size: clamp(18px, 2.5vw, 32px);
  font-weight: 325;
  line-height: clamp(1.2, 3vw, 1.5);
`;

const QuoteTextName = styled(TextBlockItem)`
  font-size: clamp(10px, 1vw, 14px);
`;

const LargeTextName = styled(TextBlockItem)`
  font-size: clamp(10px, 1vw, 14px);
`;

const BlockComponents = {
  body_text: ({ block }) => (
    <div>
      {block.body_text_heading && <Heading>{block.body_text_heading}</Heading>}
      <TextBlockItem>{block.body_text}</TextBlockItem>
    </div>
  ),
  quote_text: ({ block }) => (
    <div>
      <QuoteText>{block.quote_text}</QuoteText>
      {block.quote_text_name && (
        <QuoteTextName>{block.quote_text_name}</QuoteTextName>
      )}
    </div>
  ),
  large_text: ({ block }) => (
    <div>
      <LargeText>{block.large_text}</LargeText>
      {block.large_text_name && (
        <LargeTextName>{block.large_text_name}</LargeTextName>
      )}
    </div>
  )
};

const TextAndImageLayout = ({ position, body, image, ratio }) => (
  <TextAndImageWrapper position={position}>
    <ContentWrapper position={position}>
      <ImageWrapper ratio={ratio}>
        <Image
          fillAvailableSpace
          src={image?.filename || 'fallback-image.jpg'}
          alt={image?.alt || 'No image available'}
        />
      </ImageWrapper>
      <TextWrapper position={position}>
        {body.map((block, index) => {
          const BlockComponent = BlockComponents[block.component];
          return BlockComponent ? (
            <BlockComponent key={index} block={block} />
          ) : null;
        })}
      </TextWrapper>
    </ContentWrapper>
  </TextAndImageWrapper>
);

export default TextAndImageLayout;
